import { useTranslation } from "react-i18next";
import { IconBell, IconLoader2 } from "@tabler/icons-react";
import AppModal from "../AppModal";
import { Button } from "../../ui/Button";
import { useTrackOnMount } from "../../../hooks/use-track-on-mount";
import {
  gaEventPushOptInAnswered,
  gaEventPushOptInShown,
  type PushOptInSurface,
} from "../../../tools/analytics/domains/onboarding.analytics";
import {
  useNotificationOptIn,
  type PushOptInOutcome,
} from "../../../domains/notification/hooks/useNotificationOptIn";

/**
 * The push opt-in as a modal, shown on top of the profiling conversation the
 * moment it completes (`InterestsProfilingPage`).
 *
 * That moment is the whole point: the user has just declared the topics they
 * care about, so "I'll tell you when something lands in them" is the follow-up
 * to what they did, not an interruption. Asking earlier — during signup, where
 * the step used to live until `65f3890bd8` — spends the ONE prompt iOS grants
 * before the user has seen any value, and a refusal there is permanent short of
 * a trip through OS settings.
 *
 * The behaviour is `useNotificationOptIn`; `onDone` is what the caller wants to
 * happen NEXT (navigate, or nothing). Every exit calls it, "Maybe later"
 * included, so the caller never has to guess whether the ask is still up.
 *
 * **The ask reports itself.** Both events are fired here rather than by each
 * caller: an impression the caller forgets to fire silently turns the opt-in
 * rate into a number with no denominator, which is exactly the state the push
 * step left behind when it was removed from signup. Callers name their
 * `surface` and nothing else.
 *
 * The same copy as the full-page `NotificationPrompt`, deliberately reusing its
 * i18n keys: two layouts of one ask, not two asks.
 */
export function PushOptInModal({
  visible,
  surface,
  onDone,
}: {
  visible: boolean;
  surface: PushOptInSurface;
  onDone: (outcome: PushOptInOutcome) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { isRequesting, showOpenSettings, enable, openSettings, maybeLater } =
    useNotificationOptIn((outcome) => {
      gaEventPushOptInAnswered({ surface, outcome });
      onDone(outcome);
    });

  // The modal is mounted whether or not it is open, so visibility is the thing
  // that says there is an impression to count.
  useTrackOnMount(
    "push-opt-in-shown",
    visible ? { status: "shown", key: surface } : { status: "hidden" },
    () => {
      gaEventPushOptInShown({ surface });
    }
  );

  return (
    <AppModal
      visible={visible}
      overlayStyle={{
        backgroundColor: "rgba(86, 86, 86, 0.3)",
        backdropFilter: "blur(4px)",
      }}
      modalStyle={{
        backgroundColor: "#ffffff",
        color: "#092a23",
        padding: 24,
        gap: 24,
        borderRadius: 20,
        maxWidth: 327,
      }}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="flex size-16 items-center justify-center rounded-full bg-ds-slate-100">
          <IconBell size={32} className="text-ds-moss-600" />
        </div>
        <div className="flex flex-col gap-2 text-center">
          <h2 className="font-['DM_Sans',sans-serif] font-bold text-[20px]/7 tracking-[-0.4px] text-ds-slate-900">
            {t("Stay current with science")}
          </h2>
          <p className="font-['DM_Sans',sans-serif] text-[15px]/6 tracking-[-0.15px] text-ds-moss-600">
            {showOpenSettings
              ? t(
                  "Notifications are currently disabled. Open settings to turn them back on."
                )
              : t(
                  "We'll notify you only when impactful articles in your field are published"
                )}
          </p>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={isRequesting}
          onClick={showOpenSettings ? openSettings : enable}
        >
          {isRequesting ? (
            <IconLoader2 size={16} className="animate-spin" />
          ) : showOpenSettings ? (
            t("Open settings")
          ) : (
            t("Turn on Notifications")
          )}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          data-testid="dismiss-modal"
          disabled={isRequesting}
          onClick={maybeLater}
        >
          {t("Maybe later")}
        </Button>
      </div>
    </AppModal>
  );
}
